import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { Button, PageTitle, showNotif } from '@p81-common/ui';
import {
  Loading,
  EmptyState,
  TableTitle
} from 'components/common/Elements/Elements';
import { getRequest } from 'utils/requests';
import { useRegionalLink } from 'hooks/useRegionalLink';

import { EDGE_DETAIL_URL, EdgeDetailResponse } from './helpers/configurates';
import EdgeInfoCard from './parts/EdgeInfoCard';
import NetworksTable from './parts/NetworksTable';
import styles from './styles.module.scss';

const EdgeView = () => {
  const { t } = useTranslation('edges');
  const history = useHistory();
  const generateRegionalPath = useRegionalLink();
  const { edgeId } = useParams<{ edgeId: string }>();
  const unmount = useRef(false);

  const [edge, setEdge] = useState<EdgeDetailResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  const fetchEdgeDetail = useCallback(async () => {
    setLoading(true);
    setError(false);
    try {
      const res = await getRequest(`${EDGE_DETAIL_URL}/${edgeId}`);
      if (!unmount.current) {
        setEdge(res?.body);
      }
    } catch (err) {
      if (!unmount.current) {
        setError(true);
        showNotif(t('ERRORS.LOAD_EDGE_DETAIL_FAILED'), {
          alertProps: { severity: 'error' }
        });
      }
    } finally {
      if (!unmount.current) {
        setLoading(false);
      }
    }
  }, [edgeId, t]);

  useEffect(() => {
    fetchEdgeDetail();
    return () => {
      unmount.current = true;
    };
  }, [fetchEdgeDetail]);

  const handleBack = () => {
    history.push(generateRegionalPath('/cs/edges') as string);
  };

  if (loading) {
    return <Loading />;
  }

  if (error || !edge) {
    return <EmptyState message="connect" />;
  }

  return (
    <>
      <div className={styles.titleRow}>
        <PageTitle
          title={t('EDGE_VIEW.PAGE_TITLE')}
          subtitle={t('EDGE_VIEW.PAGE_SUBTITLE')}
          sx={{ marginBottom: 5 }}
        />
        <div className={styles.titleActions}>
          <Button variant="text" color="primary" onClick={handleBack}>
            {t('EDGE_VIEW.BACK_TO_EDGES')}
          </Button>
          {edge.grafana_url && (
            <a
              href={edge.grafana_url}
              target="_blank"
              rel="noopener noreferrer"
            >
              <Button variant="contained" color="primary">
                {t('EDGE_VIEW.OPEN_IN_GRAFANA')}
              </Button>
            </a>
          )}
          {edge.datadog_url && (
            <a
              href={edge.datadog_url}
              target="_blank"
              rel="noopener noreferrer"
            >
              <Button variant="contained" color="primary">
                {t('EDGE_VIEW.OPEN_IN_DATADOG')}
              </Button>
            </a>
          )}
        </div>
      </div>
      <TableTitle
        title={t('EDGE_VIEW.EDGE_DETAILS_TITLE')}
        withWordResult={false}
        className={styles.sectionTitle}
      />
      <EdgeInfoCard edge={edge} edgeId={edgeId} />
      <NetworksTable edgeId={edgeId} />
    </>
  );
};

export default EdgeView;
